import { type ReactNode, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { useSettingsStore } from '@shared/stores/settingsStore';
import { ToastViewport } from '@shared/ui/feedback/Toast';
import { NativePushBridge } from '@features/notifications/NativePushBridge';
import { TenantSwitchPrompt } from '@features/notifications/TenantSwitchPrompt';
import { queryClient } from '@shared/lib/queryClient';

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps): JSX.Element {
  // Track system theme preference if user hasn't picked an explicit theme.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => useSettingsStore.getState().applyTheme();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <ErrorBoundary FallbackComponent={RootErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <NativePushBridge />
        {children}
        <TenantSwitchPrompt />
        <ToastViewport />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function RootErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-paper px-6 text-center">
      <h1 className="font-display text-display-lg text-ink">Something broke.</h1>
      <p className="text-body text-ink-secondary max-w-md">
        We hit an unexpected error. Reload to try again. If it keeps happening, send the
        report below to support.
      </p>
      <pre className="rounded-md hairline bg-sunken px-4 py-3 text-mono-sm text-ink-secondary max-w-full overflow-auto">
        {error.message}
      </pre>
      <button
        type="button"
        onClick={resetErrorBoundary}
        className="rounded-sm bg-primary-600 px-5 py-2 text-label text-[color:var(--primary-ink)]"
      >
        Reload
      </button>
    </div>
  );
}

export { queryClient };
